import { Badge, DataTableColumnHeader } from "@/components/ui";
import type { MatterSurvey } from "@/lib/data/requests/matters/matter-survey";
import type { ColumnDef } from "@tanstack/react-table";

const surveyStatusLabels = {
  pending: "Awaiting response",
  in_progress: "In progress",
  reopened: "Reopened",
  completed: "Completed",
  expired: "Expired",
  cancelled: "Cancelled",
} as const satisfies Record<MatterSurvey["status"], string>;

const surveyStatusVariants = {
  pending: "blue",
  in_progress: "purple",
  reopened: "purple",
  completed: "success",
  expired: "warning",
  cancelled: "muted",
} as const satisfies Record<MatterSurvey["status"], string>;

function surveyStatus(survey: MatterSurvey) {
  return surveyStatusLabels[survey.status];
}

function formattedDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

interface MatterSurveyTableColumnsOptions {
  recipientName: (survey: MatterSurvey) => string;
  invitePending: (survey: MatterSurvey) => boolean;
  onView: (survey: MatterSurvey) => void;
}

export function matterSurveyTableColumns({
  recipientName,
  invitePending,
  onView,
}: MatterSurveyTableColumnsOptions): ColumnDef<MatterSurvey>[] {
  return [
    {
      accessorKey: "title",
      id: "Survey",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <button
          type="button"
          className="block max-w-64 min-w-32 truncate text-left font-medium"
          title={row.original.title}
          onClick={(event) => {
            event.stopPropagation();
            onView(row.original);
          }}
        >
          {row.original.title}
        </button>
      ),
    },
    {
      accessorFn: (row) => row.description ?? "",
      id: "Description",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <span
          className="text-muted-foreground block max-w-64 truncate"
          title={row.original.description ?? undefined}
        >
          {row.original.description || "—"}
        </span>
      ),
    },
    {
      accessorFn: recipientName,
      id: "Recipient",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => (
        <span
          className="block max-w-56 truncate"
          title={recipientName(row.original)}
        >
          {recipientName(row.original)}
        </span>
      ),
    },
    {
      accessorKey: "expiresAt",
      id: "Expires",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => formattedDate(row.original.expiresAt),
    },
    {
      accessorFn: surveyStatus,
      id: "Status",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => {
        const status = surveyStatus(row.original);
        return (
          <div className="flex flex-col items-start gap-1">
            <Badge
              variant={surveyStatusVariants[row.original.status]}
              className="w-fit"
            >
              {status}
            </Badge>
            {invitePending(row.original) ? (
              <Badge variant="warning" className="w-fit">
                Portal invite not accepted
              </Badge>
            ) : null}
          </div>
        );
      },
    },
  ];
}
